import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS_ACTOR, COLOR_ACTOR_PER_DEFECTE } from '../utils/actorColors';
import { toast } from 'react-toastify';
import { strategyMappingService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const StrategicEnvironment = ({ case_, onUpdate, collaboration }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [actors, setActors] = useState([]);
  // Els elements del llenç venen a la mateixa crida i no els guardava ningú.
  // Serveixen per dir què té cada actor al mapa —i, sobretot, qui encara no hi
  // és: pots tenir un actor descrit i no haver-lo posat mai al llenç, i fins
  // ara no te n'assabentaves.
  const [elements, setElements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showActorModal, setShowActorModal] = useState(false);
  const [editingActor, setEditingActor] = useState(null);
  const [actorForm, setActorForm] = useState({
    name: '',
    description: '',
    primary_intention: '',
    color: COLOR_ACTOR_PER_DEFECTE
  });

  useEffect(() => {
    if (case_?.id) {
      fetchActors();
    }
  }, [case_?.id]);

  // Listen for real-time collaboration changes
  useEffect(() => {
    // Els actors que crea o edita un company apareixen sols, sense recarregar
    // la llista sencera com abans.
    const aplicaCanvi = (event) => {
      const { entity, action, data, user } = event.detail || {};
      if (entity !== 'actor') return;
      if (!user?.user_id || !currentUser?.id || user.user_id === currentUser.id) return;
      setActors(prev => {
        if (action === 'deleted') return prev.filter(a => a.id !== data.id);
        const i = prev.findIndex(a => a.id === data.id);
        if (i === -1) return [...prev, data];
        const copia = [...prev];
        copia[i] = { ...copia[i], ...data };
        return copia;
      });
    };
    window.addEventListener('collaboration-entity-change', aplicaCanvi);
    return () => window.removeEventListener('collaboration-entity-change', aplicaCanvi);
  }, [currentUser?.id]);

  const fetchActors = async () => {
    try {
      setLoading(true);
      const data = await strategyMappingService.getStrategyMapping(case_.id);
      setActors(data.actors || []);
      setElements(data.elements || []);
    } catch (error) {
      console.error('Error fetching actors:', error);
      toast.error(t('strategicEnvironment.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateActor = async () => {
    if (!actorForm.name.trim()) {
      toast.error(t('strategicEnvironment.nameRequired'));
      return;
    }

    try {
      await strategyMappingService.createActor(case_.id, actorForm);
      
      // Send collaboration update
      
      await fetchActors();
      handleCloseModal();
    } catch (error) {
      toast.error(t('strategicEnvironment.createError'));
    }
  };

  const handleUpdateActor = async () => {
    if (!actorForm.name.trim()) {
      toast.error(t('strategicEnvironment.nameRequired'));
      return;
    }

    try {
      await strategyMappingService.updateActor(editingActor.id, actorForm);
      
      // Send collaboration update
      
      await fetchActors();
      handleCloseModal();
    } catch (error) {
      toast.error(t('strategicEnvironment.updateError'));
    }
  };

  const handleDeleteActor = async (actor) => {
    if (!window.confirm(t('strategicEnvironment.confirmDelete', { name: actor.name }))) {
      return;
    }

    try {
      await strategyMappingService.deleteActor(actor.id);
      
      // Send collaboration update
      
      await fetchActors();
    } catch (error) {
      toast.error(t('strategicEnvironment.deleteError'));
    }
  };

  const handleEditActor = (actor) => {
    setEditingActor(actor);
    setActorForm({
      name: actor.name,
      description: actor.description || '',
      primary_intention: actor.primary_intention || '',
      color: actor.color
    });
    setShowActorModal(true);
  };

  const handleCloseModal = () => {
    setShowActorModal(false);
    setEditingActor(null);
    setActorForm({
      name: '',
      description: '',
      primary_intention: '',
      color: COLOR_ACTOR_PER_DEFECTE
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600 mx-auto"></div>
          <p className="mt-2 text-neutral-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-medium text-neutral-900">
            {t('strategicEnvironment.title')}
          </h2>
          <p className="text-neutral-500 text-sm mt-1">
            {t('strategicEnvironment.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setShowActorModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 hover:border-neutral-400 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          {t('strategicEnvironment.addActor')}
        </button>
      </div>

      {/* Actors Grid */}
      {actors.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-neutral-200 rounded-lg">
          <h3 className="text-lg font-medium text-neutral-700 mb-2">
            {t('strategicEnvironment.noActors')}
          </h3>
          <p className="text-neutral-500 mb-6">
            {t('strategicEnvironment.getStarted')}
          </p>
          <button
            onClick={() => setShowActorModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 hover:border-neutral-400 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            {t('strategicEnvironment.addFirstActor')}
          </button>
        </div>
      ) : (
        /* ══════════════════════════════════════════════════════════════════
           Una fila per actor (redisseny de l'11/08/2026, opció B).

           Un entorn estratègic és, abans que res, **qui vol què**, i les
           intencions només es comparen si estan una sota l'altra. Amb targetes
           en graella, per comparar el que volen dos actors havies de mirar a
           dos llocs; i amb deu actors —que és el que passarà a l'aula— la
           graella es fa inservible.

           El **color** deixa de ser un punt decoratiu i passa a ser la franja
           que marca l'actor: és el mateix color amb què surt al llenç del
           mapeig, o sigui que aquí fa de codi i no d'adorn.
           ══════════════════════════════════════════════════════════════════ */
        <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-neutral-200 bg-neutral-50">
                  <th className="px-4 py-2.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-neutral-400">
                    {t('strategicEnvironment.actor')}
                  </th>
                  <th className="px-4 py-2.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-neutral-400">
                    {t('strategicEnvironment.primaryIntention')}
                  </th>
                  <th className="whitespace-nowrap px-4 py-2.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-neutral-400">
                    {t('strategicEnvironment.onTheMap')}
                  </th>
                  <th className="w-20 px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {actors.map((actor) => {
                  const seus = elements.filter(e => e.actor_id === actor.id);
                  const capacitats = seus.filter(e => e.element_type === 'capability').length;
                  const intencions = seus.filter(e => e.element_type === 'intention').length;
                  return (
                    <tr key={actor.id} className="border-b border-neutral-200 align-top last:border-b-0 hover:bg-neutral-50">
                      <td className="min-w-[12rem] px-4 py-3">
                        <div className="flex gap-3">
                          <span
                            className="mt-0.5 block w-[3px] flex-shrink-0 self-stretch rounded-sm"
                            style={{ backgroundColor: actor.color }}
                            aria-hidden="true"
                          />
                          <div className="min-w-0">
                            <span className="block font-medium text-neutral-900">{actor.name}</span>
                            {actor.description && (
                              <span className="mt-0.5 block text-xs text-neutral-500">{actor.description}</span>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="max-w-[52ch] px-4 py-3 leading-relaxed text-neutral-700">
                        {actor.primary_intention || (
                          <span className="text-neutral-400">{t('strategicEnvironment.noIntentionYet')}</span>
                        )}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 font-mono text-[11px] text-neutral-500">
                        {capacitats === 0 && intencions === 0 ? (
                          <span className="italic text-neutral-400">{t('strategicEnvironment.notOnMapYet')}</span>
                        ) : (
                          [capacitats && t('strategicEnvironment.capabilityCount', { count: capacitats }),
                           intencions && t('strategicEnvironment.intentionCount', { count: intencions })]
                            .filter(Boolean).join(' · ')
                        )}
                      </td>

                      <td className="px-4 py-3">
                        {/* Editar i esborrar deixen d'aparèixer només en passar-hi
                            el ratolí per sobre: en una tauleta no sortien mai. */}
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => handleEditActor(actor)}
                            title={t('common.edit')}
                            aria-label={t('common.edit')}
                            className="rounded p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteActor(actor)}
                            title={t('common.delete')}
                            aria-label={t('common.delete')}
                            className="rounded p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-error-dark"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Les oportunitats del llenç no pengen de cap actor: són del cas. Dir-ho
          evita que semblin un actor que falta. */}
      {actors.length > 0 && elements.filter(e => !e.actor_id).length > 0 && (
        <p className="text-xs text-neutral-500">
          {t('strategicEnvironment.looseOpportunities', {
            count: elements.filter(e => !e.actor_id).length
          })}
        </p>
      )}

      {/* Actor Modal */}
      {showActorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-neutral-900 mb-4">
              {editingActor ? t('strategicEnvironment.editActor') : t('strategicEnvironment.createActor')}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t('common.name')}
                </label>
                <input
                  type="text"
                  value={actorForm.name}
                  onChange={(e) => setActorForm({ ...actorForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t('strategicEnvironment.primaryIntention')}
                </label>
                <input
                  type="text"
                  value={actorForm.primary_intention}
                  onChange={(e) => setActorForm({ ...actorForm, primary_intention: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t('common.description')}
                </label>
                <textarea
                  value={actorForm.description}
                  onChange={(e) => setActorForm({ ...actorForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t('strategicEnvironment.color')}
                </label>
                <div className="space-y-3">
                  {/* Predefined Color Palette */}
                  <div className="grid grid-cols-8 gap-2">
                    {COLORS_ACTOR.map((color) => (
                      <button
                        key={color}
                        onClick={() => setActorForm({ ...actorForm, color })}
                        className={`w-6 h-6 rounded border-2 transition-all ${
                          actorForm.color === color ? 'border-neutral-800' : 'border-neutral-300'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                  
                  {/* Custom Color Picker */}
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">
                      {t('strategicEnvironment.customColor')}
                    </label>
                    <input
                      type="color"
                      value={actorForm.color}
                      onChange={(e) => setActorForm({ ...actorForm, color: e.target.value })}
                      className="w-full h-10 rounded-lg border border-neutral-300"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-neutral-700 hover:text-neutral-900"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={editingActor ? handleUpdateActor : handleCreateActor}
                disabled={!actorForm.name.trim()}
                className="px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 disabled:opacity-50"
              >
                {editingActor ? t('common.update') : t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StrategicEnvironment;